import { action, makeAutoObservable } from "mobx";

export type GridSelectionMode = "selected" | "all_matching";

export class GridSelectionStore<TId extends string | number = string | number> {
  private _selectedIds = new Set<TId>();
  private _mode: GridSelectionMode = "selected";
  private _matchingTotal: number | null = null;
  private _matchingLimit: number | null = null;
  private _lastAnchorId: TId | null = null;

  constructor() {
    makeAutoObservable(this, {
      replaceSelection: action,
      toggle: action,
      selectMany: action,
      clear: action,
      switchToAllMatching: action,
      setMatchingTotal: action,
      setMatchingLimit: action,
      setAnchor: action,
    });
  }

  get selectedIds(): TId[] {
    return Array.from(this._selectedIds);
  }

  get count(): number {
    return this._mode === "all_matching"
      ? (this._matchingLimit ?? this._matchingTotal ?? 0)
      : this._selectedIds.size;
  }

  get selectedCount(): number {
    return this._selectedIds.size;
  }

  get mode(): GridSelectionMode {
    return this._mode;
  }

  get matchingTotal(): number | null {
    return this._matchingTotal;
  }

  get matchingLimit(): number | null {
    return this._matchingLimit;
  }

  has(id: TId): boolean {
    return this._selectedIds.has(id);
  }

  get anchorId(): TId | null {
    return this._lastAnchorId;
  }

  replaceSelection(ids: TId[]) {
    this._selectedIds = new Set(ids);
    this._mode = "selected";
  }

  toggle(id: TId) {
    if (this._selectedIds.has(id)) {
      this._selectedIds.delete(id);
    } else {
      this._selectedIds.add(id);
    }
    if (this._mode === "all_matching") {
      this._mode = "selected";
    }
  }

  selectMany(ids: TId[]) {
    ids.forEach(id => this._selectedIds.add(id));
    if (this._mode === "all_matching") {
      this._mode = "selected";
    }
  }

  setAnchor(id: TId | null) {
    this._lastAnchorId = id;
  }

  clear() {
    this._selectedIds.clear();
    this._mode = "selected";
    this._matchingLimit = null;
    this._matchingTotal = null;
    this._lastAnchorId = null;
  }

  switchToAllMatching(limit?: number | null) {
    this._mode = "all_matching";
    this._matchingLimit = limit ?? this._matchingTotal;
  }

  setMatchingTotal(total: number | null) {
    this._matchingTotal = total;
    if (this._mode === "all_matching" && this._matchingLimit !== null && total !== null) {
      this._matchingLimit = Math.min(this._matchingLimit, total);
    }
  }

  setMatchingLimit(limit: number | null) {
    if (limit === null) {
      this._matchingLimit = null;
      return;
    }
    const normalizedLimit = Math.max(1, Math.floor(limit));
    this._matchingLimit =
      this._matchingTotal === null
        ? normalizedLimit
        : Math.min(normalizedLimit, this._matchingTotal);
  }
}
